import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Contract pulse posture-history surface (T-0131 / E06-F07).
 *
 * Wires the Recharts trend chart on the contract pulse tab
 * (`routes/engagements/$engagementId/contract/pulse.tsx`) to the
 * `pulse_posture_snapshots` table shipped in PR #144. The chart needs a
 * stable, time-stamped posture series independent of free-form
 * `cycle_label` text and reproducible at audit time per R6, so the
 * backend snapshots posture on every publish.
 *
 *   GET /api/v1/engagements/:engagement_id/contract/pulse/posture-history
 *       ?range=30d|90d|all
 *     → { range, items: [{ snapshot_id, captured_at, posture,
 *                          contributing_signals_count, pulse_id }] }
 *
 * Posture vocabulary is the 5-state chart palette
 * `{green, yellow_divergent, red, grey, unassessed}` — the pulse's
 * rules-based 3-state posture (green/yellow/red) is mapped server-side
 * to the chart palette (yellow → yellow_divergent; null → unassessed).
 * `grey` is reserved for a future cadence-tick path.
 *
 * Rows are returned oldest → newest so the chart x-axis reads
 * left-to-right naturally; consumers don't need to re-sort.
 */

export const POSTURE_HISTORY_RANGES = ['30d', '90d', 'all'] as const;
export type PostureHistoryRange = (typeof POSTURE_HISTORY_RANGES)[number];

export const POSTURE_HISTORY_VOCAB = [
  'green',
  'yellow_divergent',
  'red',
  'grey',
  'unassessed',
] as const;
export type PostureHistoryPosture = (typeof POSTURE_HISTORY_VOCAB)[number];

export const PulsePostureSnapshotSchema = z.object({
  snapshot_id: z.string().uuid(),
  pulse_id: z.string().uuid().nullable(),
  captured_at: z.string(),
  posture: z.enum(POSTURE_HISTORY_VOCAB),
  contributing_signals_count: z.number().int().nonnegative(),
  source_kind: z.string(),
});
export type PulsePostureSnapshot = z.infer<typeof PulsePostureSnapshotSchema>;

export const PulsePostureHistoryResponseSchema = z.object({
  range: z.enum(POSTURE_HISTORY_RANGES),
  items: z.array(PulsePostureSnapshotSchema),
});
export type PulsePostureHistoryResponse = z.infer<typeof PulsePostureHistoryResponseSchema>;

export function getPulsePostureHistory(
  engagementId: string,
  range: PostureHistoryRange = '90d',
): Promise<PulsePostureHistoryResponse> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/contract/pulse/posture-history`,
    PulsePostureHistoryResponseSchema,
    { query: { range } },
  );
}

export function usePulsePostureHistoryQuery(
  engagementId: string | undefined,
  range: PostureHistoryRange = '90d',
) {
  return useQuery({
    queryKey: ['pulses', engagementId, 'posture-history', range] as const,
    queryFn: () => getPulsePostureHistory(engagementId!, range),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}
